import { Message } from "primereact/message";

import {
  ConfirmDialog,
  Dialog,
  DialogFooter,
  DialogHeader,
  ErrorState,
  ForbiddenState,
  Icon,
  StateMessage,
} from "@/ds";
import { CampoDestinoCancelamento } from "../campo-destino-cancelamento";
import { JustificativaField } from "@/shared/components";
import { FecharTooltip, fecharPt } from "../fechar-tooltip";
import {
  CANCELAR_ESCOPO_TITLE,
  CANCELAR_LABEL,
  CARREGANDO_LABEL,
  CONFIRMACAO_MESSAGE,
  ITEM_CENTRO_CUSTO,
  ITEM_ESCOPO,
  ITEM_PROJECAO,
  ITEM_TIPO_OBRA,
} from "./cancelar-escopo-macro-dialog.constants";
import modal from "../planejamento-controle.modal.module.scss";
import styles from "./cancelar-escopo-macro-dialog.module.scss";
import type { CancelarEscopoMacroDialogViewModel } from "./types-cancelar-escopo-macro-dialog";

const FECHAR_TESTID = "cancelar-escopo-fechar-x";
const DIALOGO_PT = { footer: { className: modal.dialogoRodape }, ...fecharPt(FECHAR_TESTID) };

export function CancelarEscopoMacroDialogView({
  formId,
  estado,
  errorMessage,
  recarregar,
  escopo,
  projecaoTexto,
  confirmarLabel,
  destino,
  avisoImpacto,
  avisoSemProjecao,
  avisoUltimoEscopo,
  justificativa,
  justificativaError,
  isSubmitting,
  submitError,
  confirmarDesabilitado,
  escolherDestino,
  digitarJustificativa,
  enviar,
  cancelar,
  descarte,
}: CancelarEscopoMacroDialogViewModel) {
  return (
    <>
      <Dialog
        visible
        size="xl"
        className={modal.dialogoFicha}
        contentClassName={modal.corpo}
        onHide={cancelar}
        locked={isSubmitting}
        focusOnShow={false}
        header={<DialogHeader title={CANCELAR_ESCOPO_TITLE} />}
        pt={DIALOGO_PT}
        footer={
          <DialogFooter
            className={modal.rodape}
            cancelLabel={CANCELAR_LABEL}
            confirmLabel={confirmarLabel}
            severity="danger"
            confirmButtonProps={{
              disabled: confirmarDesabilitado,
            }}
            formId={formId}
            loading={isSubmitting}
            locked={isSubmitting}
            onCancel={cancelar}
            testId="cancelar-escopo"
          />
        }
        data-testid="cancelar-escopo-macro-dialog"
      >
        <FecharTooltip testId={FECHAR_TESTID} />
        {estado === "carregando" ? (
          <StateMessage
            variant="loading"
            title={CARREGANDO_LABEL}
            testId="cancelar-escopo-loading"
          />
        ) : null}
        {estado === "proibido" ? (
          <ForbiddenState description={errorMessage ?? undefined} testId="cancelar-escopo-error" />
        ) : null}
        {estado === "erro" ? (
          <ErrorState
            message={errorMessage ?? undefined}
            onRetry={recarregar}
            testId="cancelar-escopo-error"
          />
        ) : null}

        {estado === "pronto" ? (
          <form id={formId} className={styles.form} onSubmit={enviar} noValidate>
            <p className={styles.pergunta}>{CONFIRMACAO_MESSAGE}</p>
            <dl className={styles.itens} data-testid="cancelar-escopo-cartao">
              <div className={styles.item}>
                <dt>{ITEM_ESCOPO}</dt>
                <dd data-testid="cancelar-escopo-nome">{escopo.nome}</dd>
              </div>
              <div className={styles.item}>
                <dt>{ITEM_TIPO_OBRA}</dt>
                <dd>{escopo.tipoObra}</dd>
              </div>
              <div className={styles.item}>
                <dt>{ITEM_CENTRO_CUSTO}</dt>
                <dd data-testid="cancelar-escopo-contexto">{escopo.centroCusto}</dd>
              </div>
              <div className={styles.item}>
                <dt>{ITEM_PROJECAO}</dt>
                <dd data-testid="cancelar-escopo-projecao">{projecaoTexto}</dd>
              </div>
            </dl>

            {destino ? (
              <CampoDestinoCancelamento
                value={destino.value}
                onChange={escolherDestino}
                opcoes={destino.opcoes}
                error={destino.error}
                disabled={isSubmitting}
                testId="cancelar-escopo-destino"
                errorTestId="cancelar-escopo-destino-error"
              />
            ) : null}
            {avisoImpacto ? (
              <Message
                className="p-message-block"
                severity="warn"
                icon={<Icon name="exclamation-triangle" size={18} />}
                text={avisoImpacto}
                pt={{ text: { className: styles.alertaTexto } }}
                data-testid="cancelar-escopo-impacto"
              />
            ) : null}
            {avisoSemProjecao ? (
              <Message
                className="p-message-block"
                severity="info"
                icon={<Icon name="info-circle" size={18} />}
                text={avisoSemProjecao}
                data-testid="cancelar-escopo-sem-projecao"
              />
            ) : null}
            {avisoUltimoEscopo ? (
              <Message
                className="p-message-block"
                severity="warn"
                icon={<Icon name="exclamation-triangle" size={18} />}
                text={avisoUltimoEscopo}
                pt={{ text: { className: styles.alertaTexto } }}
                data-testid="cancelar-escopo-ultimo"
              />
            ) : null}

            <JustificativaField
              labelClassName={modal.rotuloCampo}
              rows={2}
              value={justificativa}
              onChange={digitarJustificativa}
              error={justificativaError}
              disabled={isSubmitting}
              testIds={{
                campo: "cancelar-escopo-justificativa",
                contador: "cancelar-escopo-contador",
                erro: "cancelar-escopo-justificativa-error",
              }}
            />

            {submitError ? (
              <Message
                className="p-message-block"
                severity="error"
                text={submitError}
                data-testid="cancelar-escopo-submit-error"
              />
            ) : null}
          </form>
        ) : null}
      </Dialog>
      {descarte ? <ConfirmDialog {...descarte} /> : null}
    </>
  );
}
